import React from 'react';
import { Icon } from '@icons';
import { IconSelect } from './IconSelect';
import { useScrollLock } from '../../hooks/useScrollLock';
import { ReservationResponseDto } from '@types';
import { getAllowedStatusOptions } from '@constants/reservationTransitions';

interface UserSummary { id: string; name: string; phone?: string; shippingAddress?: string }
interface EquipmentSummary { id: string; name: string }

interface Props {
  open: boolean;
  onClose: () => void;
  reservation?: ReservationResponseDto | null;
  user?: UserSummary | null;
  equipment?: EquipmentSummary | null;
  onStatusChange?: (id: string, status: string) => Promise<void> | void;
}

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return iso;
  }
};

export const ReservationModal: React.FC<Props> = ({ open, onClose, reservation, user, equipment, onStatusChange }) => {
  useScrollLock(!!open);

  // estado local para edición del estado
  const [selectedStatus, setSelectedStatus] = React.useState<string>(reservation?.status ?? '');
  React.useEffect(() => {
    setSelectedStatus(reservation?.status ?? '');
  }, [reservation?.status]);

  
  if (!open || !reservation) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
      {/* Backdrop */}
  <div className="absolute inset-0 cursor-pointer bg-black/50" onClick={onClose} />

      {/* Panel: full-width sheet on mobile, centered box on md+ */}
      <div className="relative z-10 w-full md:max-w-2xl md:mx-auto">
        <div className="md:bg-white md:rounded-lg md:shadow-lg md:max-h-[80vh] md:overflow-auto bg-white rounded-t-xl p-4 md:p-6">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold">Detalle de reserva</h3>
            <button onClick={onClose} className="p-2 ml-4 text-gray-500 rounded-full cursor-pointer hover:text-gray-700">
              <Icon name="x" size={18} />
            </button>
          </div>

          <div className="mt-3 space-y-3 text-sm text-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">Código</div>
              <div className="text-sm font-medium text-gray-900">{reservation.reservationCode}</div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Usuario</div>
              <div className="text-sm font-medium text-gray-900">{user?.name || reservation.userId}</div>
              {user?.phone && <div className="text-sm text-gray-600">Tel: {user.phone}</div>}
            </div>

            <div>
              <div className="text-xs text-gray-500">Equipo</div>
              <div className="text-sm font-medium text-gray-900">{equipment?.name || reservation.equipmentId}</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500">Fechas</div>
                <div className="text-sm text-gray-900">{formatDate(reservation.startDate)} → {formatDate(reservation.endDate)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Días</div>
                <div className="text-sm text-gray-900">{reservation.rentalDays}</div>
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Precio total</div>
              <div className="text-sm text-gray-900">{reservation.totalPrice} {reservation.currency}</div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Dirección</div>
              <div className="text-sm text-gray-900">{reservation.shippingAddress}</div>
              <div className="text-sm text-gray-600">{reservation.city} · {reservation.province} · {reservation.cp}</div>
            </div>

            {reservation.notes && (
              <div>
                <div className="text-xs text-gray-500">Notas</div>
                <div className="text-sm text-gray-900">{reservation.notes}</div>
              </div>
            )}

            <div>
              <label className="block mb-1 text-xs text-gray-500">Estado</label>
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="inline-block">
                    <span className="inline-flex items-center px-3 py-1 text-xs text-gray-800 capitalize bg-gray-100 rounded-full">
                      <Icon name={reservation.status || 'default'} size={14} className="mr-2" />
                      {reservation.status}
                    </span>
                  </div>
                </div>
                <div className="w-60">
                  <IconSelect
                    value={selectedStatus}
                    onChange={(v) => setSelectedStatus(v)}
                    openUp={true}
                    options={getAllowedStatusOptions(reservation.status as string)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end mt-5 space-x-2">
            {selectedStatus !== reservation.status && (
              <button
                onClick={async () => {
                  if (!onStatusChange) return;
                  try {
                    await onStatusChange(reservation.id, selectedStatus);
                  } catch (err) {
                    console.error('Error guardando estado:', err);
                  }
                }}
                className="px-4 py-2 text-white bg-blue-600 rounded-md cursor-pointer"
              >
                Guardar
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md cursor-pointer">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationModal;
